import { Injectable, signal } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ScrollSpyService {
  readonly activeSection = signal<string>('');
  private observer?: IntersectionObserver;
  private pendingSection: string | null = null;
  private pendingScrollTop = false;

  observeSections(ids: string[]): void {
    this.disconnect();

    if (!ids.length) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.activeSection.set(ids[0]);
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length) {
          const target = visible[0].target as HTMLElement;
          this.activeSection.set(target.dataset['spyAs'] || target.id);
        }
      },
      {
        rootMargin: '-20% 0px -55% 0px',
        threshold: [0.15, 0.35, 0.6, 0.85],
      }
    );

    ids.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        this.observer?.observe(element);
      }
    });
  }

  disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = undefined;
    }
    this.activeSection.set('');
  }

  setPendingSection(id: string): void {
    this.pendingSection = id;
  }

  consumePendingSection(): string | null {
    const pending = this.pendingSection;
    this.pendingSection = null;
    return pending;
  }

  setPendingScrollTop(): void {
    this.pendingScrollTop = true;
  }

  consumePendingScrollTop(): boolean {
    const pending = this.pendingScrollTop;
    this.pendingScrollTop = false;
    return pending;
  }

  scrollTo(id: string): void {
    const element = document.getElementById(id);
    if (element) {
      const header = document.querySelector('.site-header') as HTMLElement;
      const headerHeight = header ? header.offsetHeight : 120;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const sectionPaddingTop = 72; // approximate padding-top of scroll-section
      const offsetPosition = elementPosition + sectionPaddingTop - headerHeight;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  isActive(id: string): boolean {
    return this.activeSection() === id;
  }
}
